// ==============================|| C-7 — tenant custom CSS (Enterprise) ||============================== //
// A tenant admin may supply free-form CSS to fine-tune their white-label look. It is injected into
// the document at runtime, so it MUST be sanitized: the real XSS vector is a `</style>` breakout
// that escapes the <style> element into arbitrary HTML/script. We also strip CSS features that
// enable remote loads / script execution. Trust model: tenant-scoped Enterprise setting, but we
// never rely on trust — the sanitizer is the boundary and is unit-tested.

const STYLE_ELEMENT_ID = 'zc-tenant-custom-css';
const MAX_LENGTH = 50_000;

/**
 * Neutralize the dangerous parts of tenant-supplied CSS before it is injected into a <style>:
 *  - remove ALL `<`/`>` — CSS never needs them, and this kills `</style>…<script>` breakouts
 *    and HTML-comment tricks outright.
 *  - strip `@import` (remote stylesheet loads / data exfiltration channel).
 *  - strip legacy script vectors: `expression(` (IE), `behavior:`/`-ms-behavior:` (IE .htc),
 *    and `javascript:` URLs.
 *  - cap length so a pathological blob can't bloat the DOM.
 * Returns the cleaned CSS (possibly empty).
 */
export function sanitizeCustomCss(raw: string | undefined | null): string {
  if (!raw) return '';
  let css = raw.slice(0, MAX_LENGTH);

  // Kill tag breakouts + HTML comments in one stroke — CSS has no legitimate use for < or >.
  css = css.replace(/[<>]/g, '');
  // Remote loads.
  css = css.replace(/@import[^;]*;?/gi, '');
  // Legacy IE script execution vectors.
  css = css.replace(/expression\s*\(/gi, '');
  css = css.replace(/(-ms-)?behavior\s*:/gi, '');
  // javascript: (and vbscript:) URLs, even if obfuscated with whitespace.
  css = css.replace(/(javascript|vbscript)\s*:/gi, '');

  return css.trim();
}

/**
 * Inject (or update/remove) the tenant's sanitized custom CSS as a single managed <style> in
 * <head>. Idempotent: repeated calls replace the same element. Empty/blank CSS removes it.
 */
export function applyCustomCss(raw: string | undefined | null): void {
  if (typeof document === 'undefined') return; // SSR / test guard

  const css = sanitizeCustomCss(raw);
  let el = document.getElementById(STYLE_ELEMENT_ID) as HTMLStyleElement | null;

  if (!css) {
    el?.remove();
    return;
  }

  if (!el) {
    el = document.createElement('style');
    el.id = STYLE_ELEMENT_ID;
    document.head.appendChild(el);
  }
  // textContent (never innerHTML): the browser still parses it as CSS inside <style>, but the
  // string is treated as text, so no markup is interpreted during assignment.
  el.textContent = css;
}
